import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import "./ReportSapCapisquadra.css";
import "./AnagraficaCapisquadra.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/report-sap-capisquadra`;

const selectMenuPortalStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };
const portalTarget = typeof document !== "undefined" ? document.body : null;

// Valore del filtro caposquadra per le attività senza caposquadra.
const SENZA = "__senza__";

// Anagrafica del Report Capisquadra (ex pop-up): per ogni attività del report si sceglie il
// caposquadra (tipologia GECOS "Caposquadra ..."). La community Interacta in cui si pubblica
// si ricava dal nome del caposquadra, qui non si configura.
// È la tab "Anagrafica" della pagina del report (task 500): header e "Indietro" sono della
// pagina, che tramite `onDirtyChange` sa se ci sono modifiche da salvare prima di cambiare tab.
const AnagraficaCapisquadra = ({ onDirtyChange }) => {

    const [rows, setRows] = useState([]);               // [{ attivita_id, nome, albero, commessa, coordinamento_id }]
    const [tipologie, setTipologie] = useState([]);     // [{ id, label }]
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dirty, setDirty] = useState(() => new Set());
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState("");
    const [filtroCapo, setFiltroCapo] = useState(null);
    const [filtroCommessa, setFiltroCommessa] = useState([]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [a, t] = await Promise.all([axios.get(`${BASE}/anagrafica`), axios.get(`${BASE}/tipologie`)]);
            setRows(Array.isArray(a.data) ? a.data : []);
            setTipologie(Array.isArray(t.data) ? t.data : []);
            setDirty(new Set());
        } catch (err) {
            console.error("Errore anagrafica:", err);
            setError(err.response?.data?.error || "Errore nel recupero dell'anagrafica.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    useEffect(() => { if (onDirtyChange) onDirtyChange(dirty.size > 0); }, [dirty, onDirtyChange]);
    // Uscendo dalla tab le modifiche non salvate si perdono (la pagina ha già chiesto conferma).
    useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);

    const tipologiaLabel = useMemo(() => new Map(tipologie.map((t) => [String(t.id), t.label])), [tipologie]);

    const capoFilterOptions = useMemo(() => [
        { value: SENZA, label: "— Senza caposquadra —" },
        ...tipologie.map((t) => ({ value: String(t.id), label: t.label })),
    ], [tipologie]);

    const commessaOptions = useMemo(() => {
        const set = new Set();
        rows.forEach((r) => r.commessa && set.add(r.commessa));
        return Array.from(set).sort().map((v) => ({ value: v, label: v }));
    }, [rows]);

    const senzaCaposquadra = useMemo(() => rows.filter((r) => !r.coordinamento_id).length, [rows]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const comSet = new Set(filtroCommessa.map((o) => o.value));
        return rows.filter((r) => {
            if (filtroCapo) {
                if (filtroCapo.value === SENZA ? !!r.coordinamento_id : String(r.coordinamento_id) !== filtroCapo.value) return false;
            }
            if (comSet.size && !comSet.has(r.commessa)) return false;
            if (q && ![r.nome, r.albero, r.commessa].some((v) => (v || "").toLowerCase().includes(q))) return false;
            return true;
        });
    }, [rows, search, filtroCapo, filtroCommessa]);

    const handleChangeCapo = (attivitaId, valueStr) => {
        setRows((prev) => prev.map((r) => (r.attivita_id === attivitaId ? { ...r, coordinamento_id: valueStr || null } : r)));
        setDirty((prev) => new Set(prev).add(attivitaId));
    };

    const handleSave = async () => {
        if (saving || dirty.size === 0) return;
        setSaving(true);
        try {
            const mappings = rows.filter((r) => dirty.has(r.attivita_id)).map((r) => ({
                attivita_id: r.attivita_id,
                coordinamento_id: r.coordinamento_id,
                coordinamento_label: r.coordinamento_id ? (tipologiaLabel.get(String(r.coordinamento_id)) || null) : null,
            }));
            await axios.put(`${BASE}/anagrafica`, { mappings });
            await fetchAll();
        } catch (err) {
            console.error("Errore salvataggio anagrafica:", err);
            alert(err.response?.data?.error || "Errore nel salvataggio dell'anagrafica.");
        } finally {
            setSaving(false);
        }
    };

    // Scarta le modifiche non salvate ricaricando l'anagrafica.
    const handleDiscard = () => {
        if (!window.confirm("Annullare le modifiche non salvate?")) return;
        fetchAll();
    };

    return (
        <div className="anagrafica-capisquadra">
            {/* Filtri fissi in alto mentre la tab scorre sulla tabella */}
            <div className="anagrafica-capisquadra-top">
                {error && <div className="report-sap-error" role="alert">{error}</div>}

                <div className="filter-panel anagrafica-capisquadra-filterbar">
                    <div className="filter-grid anagrafica-capisquadra-filters">
                        <div className="filter-group">
                            <label>Cerca</label>
                            <input type="text" className="anagrafica-capisquadra-input" placeholder="Nome, albero o commessa..." value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>Caposquadra</label>
                            <Select
                                isClearable
                                placeholder="Tutti"
                                options={capoFilterOptions}
                                value={filtroCapo}
                                onChange={(o) => setFiltroCapo(o || null)}
                                classNamePrefix="filter-select"
                                menuPortalTarget={portalTarget}
                                styles={selectMenuPortalStyles}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Commessa</label>
                            <Select
                                isMulti
                                placeholder="Tutte le commesse"
                                options={commessaOptions}
                                value={filtroCommessa}
                                onChange={(s) => setFiltroCommessa(s || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                                menuPortalTarget={portalTarget}
                                styles={selectMenuPortalStyles}
                            />
                        </div>
                    </div>
                    {!loading && (
                        <div className="anagrafica-capisquadra-count">
                            {filteredRows.length} di {rows.length} attività
                            {senzaCaposquadra > 0 && (
                                <button type="button" className="anagrafica-capisquadra-link" onClick={() => setFiltroCapo(capoFilterOptions[0])}>
                                    {senzaCaposquadra} senza caposquadra
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <section className="report-sap-localita-card anagrafica-capisquadra-section">
                {loading ? (
                    <Lf23Loader testo="Caricamento anagrafica..." />
                ) : (
                    <>
                        <div className="report-sap-table-wrap">
                            <table className="report-sap-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "24%" }}>Attività</th>
                                        <th style={{ width: "28%" }}>Albero</th>
                                        <th style={{ width: "18%" }}>Commessa</th>
                                        <th style={{ width: "30%" }}>Caposquadra</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((r) => (
                                        <tr key={r.attivita_id} className={dirty.has(r.attivita_id) ? "anagrafica-capisquadra-row--dirty" : ""}>
                                            <td className="report-sap-servizio-cell">{r.nome}</td>
                                            <td>{r.albero || "—"}</td>
                                            <td>{r.commessa || "—"}</td>
                                            <td>
                                                <select
                                                    className={`anagrafica-capisquadra-select ${r.coordinamento_id ? "" : "anagrafica-capisquadra-select--empty"}`}
                                                    value={r.coordinamento_id ?? ""}
                                                    onChange={(e) => handleChangeCapo(r.attivita_id, e.target.value)}
                                                >
                                                    <option value="">— Nessun caposquadra —</option>
                                                    {/* caposquadra non più attivo in GECOS ma ancora assegnato: resta visibile */}
                                                    {r.coordinamento_id && !tipologiaLabel.has(String(r.coordinamento_id)) && (
                                                        <option value={r.coordinamento_id}>Caposquadra non attivo (#{r.coordinamento_id})</option>
                                                    )}
                                                    {tipologie.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </section>

            {/* Barra modifiche: separata dai filtri, compare solo con modifiche da salvare */}
            {dirty.size > 0 && (
                <div className="anagrafica-capisquadra-savebar" role="status">
                    <span className="anagrafica-capisquadra-savebar-text">
                        {dirty.size === 1 ? "1 modifica all'anagrafica non salvata" : `${dirty.size} modifiche all'anagrafica non salvate`}
                    </span>
                    <div className="anagrafica-capisquadra-savebar-actions">
                        <button type="button" className="report-sap-btn-secondary" onClick={handleDiscard} disabled={saving}>
                            Annulla modifiche
                        </button>
                        <button type="button" className="report-sap-btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? "Salvo..." : "Salva modifiche"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnagraficaCapisquadra;
